import React from "react";
import { Button, makeStyles, Text, typographyStyles } from "@fluentui/react-components";
import { Dismiss12Regular } from "@fluentui/react-icons";
import classNames from "classnames";
import { isRemovalGroup, getRemovalDisplayName, getRemovalChipLabel } from "src/features/selectors";
import { OutlineStyleCount } from "./OutlineStyleCount";
import { StyleChip } from "../style-chip";
import "./OutlineStyle.css";

import type { TOutlineGroup, TStyle } from "./types";
import type { CharacterStyleProfileEntry, OutlineMode, StyleDefinition } from "src/types";

const useStyles = makeStyles({
  text: typographyStyles.body1,
});

export interface OutlineStyleProps extends TStyle, React.HTMLAttributes<HTMLDivElement> {
  characterStyles?: CharacterStyleProfileEntry[] | null;
  group?: TOutlineGroup;
  ignoreParagraphs?: boolean;
  outlineMode: OutlineMode;
  styleDefinitionMap?: Record<string, StyleDefinition> | null;
  onClearCharacterStyle?: () => void;
  onClearStyle?: () => void;
  onSelectCharacterStyle?: (name: string | null) => void;
  onSelectStyle?: (group: string, style: string | null) => void;
  onSetBoundaryIndices?: (boundaryIndices: [number, number]) => void;
  selected: (style: string) => boolean;
}

export function OutlineStyle({
  changes,
  characterStyles,
  group,
  ignoreParagraphs,
  name,
  outlineMode,
  styleDefinitionMap,
  total,
  onClearCharacterStyle,
  onClearStyle,
  onSelectCharacterStyle,
  onSelectStyle,
  onSetBoundaryIndices,
  selected,
  ...props
}: OutlineStyleProps) {
  const styles = useStyles();

  const isRemoval = isRemovalGroup(name);
  const fullName = isRemoval
    ? (getRemovalDisplayName(name, characterStyles) ?? name)
    : (styleDefinitionMap?.[name]?.full_name ?? name);
  const chipLabel = isRemoval ? (getRemovalChipLabel(name) ?? name) : name;
  const ignored = ignoreParagraphs && name === "p";
  const notInProfile = !isRemoval && !styleDefinitionMap?.[name];

  const handleClick = () => {
    if (outlineMode === "character") {
      onSelectCharacterStyle?.(name);
    } else {
      if (group) {
        onSelectStyle?.(group.name, name);
      }
      if (group?.boundaryIndices) {
        onSetBoundaryIndices?.(group.boundaryIndices);
      }
    }
  };

  return (
    <div
      className={classNames("outline-style", {
        ignored,
        "outline-style-not-in-profile": notInProfile,
        selected: selected(name),
      })}
      onClick={handleClick}
      {...props}
    >
      <StyleChip ignored={ignored} notInProfile={notInProfile} removal={isRemoval}>
        {chipLabel}
      </StyleChip>
      <Text className={styles.text}>{fullName}</Text>
      <OutlineStyleCount style="default" total={total} changes={changes} />
      {selected(name) && (
        <Button
          appearance="transparent"
          icon={<Dismiss12Regular />}
          onClick={(e) => {
            e.stopPropagation();
            onClearCharacterStyle?.();
            onClearStyle?.();
          }}
          style={{ border: 0, padding: "0px", maxWidth: "20px", minWidth: "20px" }}
        />
      )}
    </div>
  );
}
